/* ============================================================
   Drag-to-reorder for a list of rows. Commits through the store's `moveTask` /
   `moveHabit`, which write ONE fractional rank per drag (docs/task-ordering.md)
   — the list passed in must be the list as it reads before the move, which is
   exactly what the page renders. Mouse, touch and keyboard sensors; a short
   press distance so a click on the row's controls isn't mistaken for a drag,
   and a hold on touch so a scroll isn't either.
   ============================================================ */
import React from 'react';
import { closestCenter, DndContext, type DragEndEvent, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export function SortableList<T extends { id: string }>({
  items,
  onMove,
  render,
  layout = 'list',
  className = '',
}: {
  items: T[];
  onMove: (from: number, to: number) => void;
  render: (item: T, index: number) => React.ReactNode;
  /** `grid` drags the whole item (tiles); `list` shows a grip at the row's edge */
  layout?: 'list' | 'grid';
  className?: string;
}) {
  // a mouse drags after 6px; a finger after a short hold, so a swipe over the
  // list still scrolls it and a tap still opens the row
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.id === active.id);
    const to = items.findIndex((i) => i.id === over.id);
    if (from >= 0 && to >= 0) onMove(from, to);
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={layout === 'grid' ? rectSortingStrategy : verticalListSortingStrategy}>
        <div className={className}>
          {items.map((item, index) => (
            <SortableRow key={item.id} id={item.id} whole={layout === 'grid'}>{render(item, index)}</SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({ id, whole, children }: { id: string; whole: boolean; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  if (whole) {
    return (
      <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }} {...attributes} {...listeners}>
        {children}
      </div>
    );
  }
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      className="group/drag relative"
    >
      <button
        type="button"
        aria-label="Drag to reorder"
        className="absolute right-1.5 top-1/2 z-[1] -translate-y-1/2 cursor-grab rounded-md p-1 text-tertiary opacity-0 transition-opacity hover:bg-card-sunk group-hover/drag:opacity-100 focus:opacity-100 active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden>
          <circle cx="2.5" cy="3" r="1.5" /><circle cx="7.5" cy="3" r="1.5" /><circle cx="2.5" cy="8" r="1.5" /><circle cx="7.5" cy="8" r="1.5" /><circle cx="2.5" cy="13" r="1.5" /><circle cx="7.5" cy="13" r="1.5" />
        </svg>
      </button>
      {children}
    </div>
  );
}
